'use client'

import React, { useState, useCallback, useEffect } from 'react';
import { appApi, ApiError } from '@/infrastructure/api';

export interface UseApiState<T> { data: T | null; loading: boolean; error: ApiError | null }
export interface UseApiOptions {
  immediate?: boolean
  onSuccess?: (data: any) => void
  onError?: (error: ApiError) => void
  /** Keep last good payload when a refresh fails (desk surfaces). */
  preserveDataOnError?: boolean
  /** Keep the previous error banner while a quiet refresh is in flight. */
  preserveErrorOnRefresh?: boolean
}

export function useApi<T = any>(apiCall: () => Promise<T>, options: UseApiOptions = {}) {
  const { immediate = false, onSuccess, onError, preserveDataOnError, preserveErrorOnRefresh } = options;
  const [state, setState] = useState<UseApiState<T>>({ data: null, loading: false, error: null });
  const execute = useCallback(async () => {
    setState(prev => ({
      ...prev,
      loading: true,
      error: preserveErrorOnRefresh ? prev.error : null,
    }));
    try { const data = await apiCall(); setState({ data, loading: false, error: null }); onSuccess?.(data) }
    catch (error) {
      const apiError = error as ApiError;
      setState(prev => ({
        data: preserveDataOnError ? prev.data : null,
        loading: false,
        error: apiError,
      }));
      onError?.(apiError)
    }
  }, [apiCall, onSuccess, onError, preserveDataOnError, preserveErrorOnRefresh]);
  const reset = useCallback(() => { setState({ data: null, loading: false, error: null }) }, []);
  useEffect(() => { if (immediate) { void execute() } }, [immediate, execute]);
  return { ...state, execute, reset };
}

export function useTradingApi() {
  return React.useMemo(() => ({
    getAccount: () => appApi.trading.getAccount(),
    getOrders: () => appApi.trading.getOrders(),
    getOrder: (orderId: string) => appApi.trading.getOrder(orderId),
    createOrder: (orderData: any) => appApi.trading.createOrder(orderData),
    cancelOrder: (orderId: string) => appApi.trading.cancelOrder(orderId),
    updateOrder: (orderId: string, updates: any) => appApi.trading.updateOrder(orderId, updates),
    getPositions: () => appApi.trading.getPositions(),
    getPosition: (symbol: string) => appApi.trading.getPosition(symbol),
    getPortfolio: () => appApi.trading.getPortfolio(),
    getAnalytics: () => appApi.trading.getAnalytics(),
    getPerformance: () => appApi.trading.getPerformance(),
    getRebalancingRecommendations: () => appApi.trading.getRebalancingRecommendations(),
    executeRebalancing: (data: any) => appApi.trading.executeRebalancing(data),
  }), []);
}

export function useAIModelsApi() {
  return React.useMemo(() => ({
    getModels: () => appApi.aiModels.getModels(),
    getModel: (modelId: string) => appApi.aiModels.getModel(modelId),
    createModel: (modelData: any) => appApi.aiModels.createModel(modelData),
    updateModel: (modelId: string, updates: any) => appApi.aiModels.updateModel(modelId, updates),
    deleteModel: (modelId: string) => appApi.aiModels.deleteModel(modelId),
    getTrainingJobs: () => appApi.aiModels.getTrainingJobs(),
    getTrainingJob: (jobId: string) => appApi.aiModels.getTrainingJob(jobId),
    startTraining: (trainingData: any) => appApi.aiModels.startTraining(trainingData),
    cancelTraining: (jobId: string) => appApi.aiModels.cancelTraining(jobId),
    getPredictions: () => appApi.aiModels.getPredictions(),
    getPrediction: (predictionId: string) => appApi.aiModels.getPrediction(predictionId),
    makePrediction: (predictionData: any) => appApi.aiModels.makePrediction(predictionData),
    getFeatures: () => appApi.aiModels.getFeatures(),
    getFeature: (featureId: string) => appApi.aiModels.getFeature(featureId),
    createFeature: (featureData: any) => appApi.aiModels.createFeature(featureData),
    updateFeature: (featureId: string, updates: any) => appApi.aiModels.updateFeature(featureId, updates),
    deleteFeature: (featureId: string) => appApi.aiModels.deleteFeature(featureId),
    getModelPerformance: (modelId: string) => appApi.aiModels.getModelPerformance(modelId),
    exportModel: (modelId: string) => appApi.aiModels.exportModel(modelId),
    importModel: (modelFile: File) => appApi.aiModels.importModel(modelFile),
  }), []);
}

export function useSystemApi() { return React.useMemo(() => ({ healthCheck: () => appApi.system.healthCheck(), getSystemInfo: () => appApi.system.getSystemInfo() }), []) }
export function useAuth() { const setAuthToken = useCallback((token: string) => { appApi.setAuthToken(token) }, []); const clearAuthToken = useCallback(() => { appApi.clearAuthToken() }, []); return { setAuthToken, clearAuthToken } }


